<template>
  <!-- Table Container Card -->
  <b-overlay :show="loading" rounded="sm" no-fade>
    <div class="mt-1"></div>
    <div>
      <b-card class="mt-1">
        <!-- Week navigation -->
        <div class="week-nav">
          <b-button variant="outline-secondary" size="sm" class="week-arrow" aria-label="Previous week" @click="shiftWeek(-1)">
            <feather-icon icon="ChevronLeftIcon" />
          </b-button>
          <div class="week-label">
            <span class="week-title">Week of {{ weekRangeLabel }}</span>
            <small class="text-muted">Week ending {{ weekEnding }}</small>
          </div>
          <b-button variant="outline-secondary" size="sm" class="week-arrow" aria-label="Next week" @click="shiftWeek(1)">
            <feather-icon icon="ChevronRightIcon" />
          </b-button>
          <b-button variant="flat-primary" size="sm" :disabled="isThisWeek" @click="goToDate(new Date())">This week</b-button>
          <flat-pickr
            v-model="pickedDate"
            :config="{ dateFormat: 'Y-m-d', locale: { firstDayOfWeek: 1 } }"
            class="form-control form-control-sm week-picker"
            placeholder="Jump to a date…"
          />
          <b-form-checkbox v-model="showAllJobs" switch class="ml-md-auto">Show completed jobs</b-form-checkbox>
        </div>

        <b-row v-for="user in timesheetData" :key="user.id">
          <b-col cols="12" :class="{ 'own-section': isOwn(user) && !ownOnly }">
            <table class="table table-sm mt-1 text-center timesheet-table" :class="isCollapsed(user) ? 'mb-0' : 'mb-3'">
              <thead>
                <tr>
                  <th colspan="12" class="p-0">
                    <button
                      type="button"
                      class="user-toggle"
                      :aria-expanded="!isCollapsed(user)"
                      @click="toggleUser(user)"
                    >
                      <feather-icon :icon="isCollapsed(user) ? 'ChevronRightIcon' : 'ChevronDownIcon'" size="16" />
                      <span>{{ user.name }}</span>
                      <span v-if="isOwn(user)" class="you-tag">You</span>
                      <span class="user-week-total" :class="{ 'has-hours': userTotal(user) > 0 }">{{ formatHours(userTotal(user)) }} h this week</span>
                      <span v-if="materialCount(user)" class="user-week-total has-hours">
                        · {{ materialCount(user) }} {{ materialCount(user) === 1 ? 'material' : 'materials' }}<template v-if="userMaterialsCost(user)"> · {{ formatMoney(userMaterialsCost(user)) }}</template>
                      </span>
                    </button>
                  </th>
                </tr>
              </thead>

              <tbody v-show="!isCollapsed(user)">
                <tr>
                  <th scope="col" style="width:12%" class="text-dark bg-light">Type</th>
                  <th v-for="day in days" :key="day.key" scope="col" style="width:7.5%" class="text-dark bg-light">
                    {{ day.label }}
                    <small class="d-block day-date">{{ day.date }}</small>
                  </th>
                  <th scope="col" style="width:24%" class="text-dark bg-light">Job</th>
                  <th scope="col" style="width:13%" class="text-dark bg-light">Part/Category</th>
                  <th scope="col" style="width:6%" class="text-dark bg-light"></th>
                </tr>

                <tr v-for="(row, index) in user.jobData" :key="row.id || `new-${index}`" class="text-center">
                  <td>
                    <div class="billable-toggle" role="radiogroup" aria-label="Billable or non-billable">
                      <button
                        type="button"
                        role="radio"
                        :aria-checked="!!row.billable"
                        :class="{ active: !!row.billable }"
                        :disabled="!row.editable"
                        @click="setBillable(row, user, true)"
                      >Billable</button>
                      <button
                        type="button"
                        role="radio"
                        :aria-checked="!row.billable"
                        :class="{ active: !row.billable, off: true }"
                        :disabled="!row.editable"
                        @click="setBillable(row, user, false)"
                      >Non-billable</button>
                    </div>
                  </td>

                  <td v-for="day in days" :key="day.key">
                    <input
                      :value="Number(row[day.key]) ? row[day.key] : ''"
                      type="number"
                      min="0"
                      :step="0.1"
                      :disabled="!row.editable"
                      :data-cell="`${user.id}-${index}-${day.key}`"
                      class="quoteInput form-control"
                      @input="row[day.key] = $event.target.value"
                      @change="saveRowData(row, user)"
                      @keydown.enter.prevent="onEnter(user, index, day.key)"
                    />
                  </td>

                  <td class="text-left">
                    <job-select
                      v-if="row.billable"
                      v-model="row.jobId"
                      :jobs="jobs"
                      :show-all-jobs="showAllJobs"
                      :disabled="!row.editable"
                      @input="row.category = null; saveRowData(row, user)"
                    />
                    <input v-else v-model="row.reason" type="text" placeholder="Reason" class="quoteInput form-control" @input="debouncedSaveRowData(row, user)" />
                  </td>

                  <td>
                    <v-select v-if="!row.billable" v-model="row.category" :options="nonBillableCategories" :clearable="false" @input="saveRowData(row, user)" />
                    <v-select v-else v-model="row.category" :disabled="!row.jobId || !row.editable" :options="getOptions(row.jobId)" :clearable="false" @input="saveRowData(row, user)" />
                  </td>

                  <td class="text-nowrap">
                    <span
                      v-if="row.finalized"
                      v-b-tooltip.hover.top="finalizedTitle(row)"
                      class="finalized-flag"
                      :class="{ locked: !row.editable }"
                      :aria-label="finalizedTitle(row)"
                    >
                      <feather-icon icon="LockIcon" />
                    </span>
                    <span class="save-state" :class="row.saveState" :title="saveStateTitle(row)">
                      <b-spinner v-if="row.saveState === 'saving'" small />
                      <feather-icon v-else-if="row.saveState === 'saved'" icon="CheckIcon" />
                      <feather-icon v-else-if="row.saveState === 'error'" icon="AlertCircleIcon" />
                    </span>
                    <b-button :disabled="!row.editable" class="btn btn-sm" variant="danger" aria-label="Delete row" @click="deleteRow(user, row)">X</b-button>
                  </td>
                </tr>

                <tr style="background-color: lightgray;">
                  <td></td>
                  <td v-for="day in days" :key="day.key" class="text-center">
                    <span
                      v-if="dayTotal(user, day.key) > 8"
                      v-b-tooltip.hover.top="`${formatHours(dayTotal(user, day.key))} hours on ${day.label} ${day.date}, more than 8`"
                      class="over-eight"
                    >{{ formatHours(dayTotal(user, day.key)) }}</span>
                    <span v-else>{{ dayTotal(user, day.key) ? formatHours(dayTotal(user, day.key)) : '' }}</span>
                  </td>
                  <td></td>
                  <td class="text-center">
                    <b-button class="btn btn-sm" variant="success" @click="addNewRow(user)">Add new</b-button>
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            <timesheet-materials
              v-show="!isCollapsed(user)"
              :user="user"
              :week-ending="weekEnding"
              :jobs="jobs"
              :show-all-jobs="showAllJobs"
              :parts-by-job="materials"
              :catalogue="materialCatalogue"
              :units="materialUnits"
              @material-created="addCatalogueMaterial"
            />
          </b-col>
        </b-row>

        <b-row class="mt-2">
          <b-col cols="12" class="text-right">
            <small class="text-muted mr-2">Tip: press Enter in a day cell to move down, or to add a row on the last line.</small>
            <b>Week Total: {{ formatHours(weekTotal) }} hours</b>
            <b v-if="weekMaterialsCost"> · Materials: {{ formatMoney(weekMaterialsCost) }}</b>
          </b-col>
        </b-row>
      </b-card>
    </div>
  </b-overlay>
</template>

<script>
import { BOverlay, BCard, BRow, BCol, BButton, BFormCheckbox, BSpinner, VBTooltip } from 'bootstrap-vue';
import { getUserData } from '@/auth/utils';
import store from '@/store';
import { onUnmounted } from '@vue/composition-api';
import userStoreModule from '../userStoreModule';
import vSelect from 'vue-select';
import flatPickr from 'vue-flatpickr-component';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import debounce from 'lodash/debounce';
import JobSelect from './JobSelect.vue';
import TimesheetMaterials from './TimesheetMaterials.vue';
import { ymd, parseYmd, addDays, weekEndingFor } from './timesheetDates';

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const DAY_KEYS = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
];

// Non-billable Part/Category options.
const NON_BILLABLE_CATEGORIES = [
  'Job Design Consultation - First 45 Min',
  'Admin - Quoting',
  'Admin - Stock Ordering',
  'Machine Maintenance',
  'Instruction / Training on Machines',
  'Advisory for External Clients',
  'Holiday / Vacation',
  'Sick / Absent',
  'Admin - Non-Service Center',
  'Other - Non-Service Center',
  'Consultation - Non-Service Center',
];

// Collapse choices used to be remembered here, which would override the default
// (your own timesheet open, everyone else collapsed). Clear any leftover value.
try {
  localStorage.removeItem('timesheetCollapsedUsers');
} catch (e) {
  // Storage can be unavailable; nothing to clear then.
}

function blankRow() {
  return {
    editable: true,
    billable: true,
    reason: null,
    category: null,
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    jobId: null,
  };
}

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BButton,
    BFormCheckbox,
    BSpinner,
    vSelect,
    flatPickr,
    JobSelect,
    TimesheetMaterials,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },

  props: {
    // Used on the Home page: show only the signed-in user's timesheet.
    ownOnly: { type: Boolean, default: false },
  },

  setup() {
    const USER_APP_STORE_MODULE_NAME = 'user';

    if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule);

    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME)) store.unregisterModule(USER_APP_STORE_MODULE_NAME);
    });
    return {};
  },

  data() {
    return {
      loading: true,
      weekEnding: weekEndingFor(new Date()),
      pickedDate: null,
      showAllJobs: false,
      jobs: [],
      timesheetData: [],
      // Each job's part names for the Part/Category column (the API calls it `materials`).
      materials: [],
      // Active catalogue materials and allowed units for the Materials section.
      materialCatalogue: [],
      materialUnits: ['pcs'],
      nonBillableCategories: NON_BILLABLE_CATEGORIES,
      currentUserId: (getUserData() || {}).id,
      // Users opened or closed by hand on this visit ({ userId: collapsed }); kept while changing weeks.
      collapseChoices: {},
    };
  },

  computed: {
    days() {
      const monday = addDays(parseYmd(this.weekEnding), -6);
      return DAY_KEYS.map((day, i) => {
        const date = addDays(monday, i);
        return { ...day, date: `${date.getMonth() + 1}/${date.getDate()}` };
      });
    },
    weekRangeLabel() {
      const end = parseYmd(this.weekEnding);
      const start = addDays(end, -6);
      const month = (d) => d.toLocaleDateString('en-US', { month: 'short' });
      const startText = `${month(start)} ${start.getDate()}`;
      const endText = start.getMonth() === end.getMonth() ? `${end.getDate()}` : `${month(end)} ${end.getDate()}`;
      return `${startText} – ${endText}, ${end.getFullYear()}`;
    },
    isThisWeek() {
      return this.weekEnding === weekEndingFor(new Date());
    },
    weekTotal() {
      return this.timesheetData.reduce((total, user) => total + this.userTotal(user), 0);
    },
    weekMaterialsCost() {
      return this.timesheetData.reduce((total, user) => total + this.userMaterialsCost(user), 0);
    },
  },

  watch: {
    pickedDate(value) {
      if (!value) return;
      this.goToDate(parseYmd(value));
      this.$nextTick(() => { this.pickedDate = null; });
    },
  },

  created() {
    // Saves for the same row run one after another, so a new row is never created twice.
    this.saveQueues = new WeakMap();
    this.getJobNumbers();
  },

  methods: {
    debouncedSaveRowData: debounce(function (row, user) {
      this.saveRowData(row, user);
    }, 500),

    toast(title, text, variant) {
      this.$toast({ component: ToastificationContent, props: { title, text, icon: 'AlertTriangleIcon', variant } });
    },

    // The server explains refusals (another person's row, a job with finalized billing); show that when given.
    errorMessage(error, fallback) {
      const data = error && error.response && error.response.data;
      return (data && data.message) || fallback;
    },

    getJobNumbers() {
      this.loading = true;
      store
        .dispatch('user/getJobNumbers', { weekEnding: this.weekEnding })
        .then((response) => {
          const jobs = response.data.jobs || (response.data.jobNumbers || []).map((jobnumber) => ({ jobnumber, active: true }));
          this.jobs = jobs.map((job) => ({ ...job, label: String(job.jobnumber) }));
          // Your own timesheet first, everyone else in the original order.
          const data = response.data.timesheetData || [];
          // Set before the data becomes reactive, so adding a material updates the page.
          data.forEach((u) => {
            if (!Array.isArray(u.materialEntries)) u.materialEntries = [];
          });
          const own = data.filter((u) => u.id === this.currentUserId);
          this.timesheetData = this.ownOnly ? own : [...own, ...data.filter((u) => u.id !== this.currentUserId)];
          this.materials = response.data.materials || [];
          this.materialCatalogue = response.data.materialCatalogue || [];
          this.materialUnits = response.data.materialUnits && response.data.materialUnits.length ? response.data.materialUnits : ['pcs'];
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          console.error(error);
          this.toast('Timesheet not loaded', 'Please refresh the page and try again.', 'danger');
        });
    },

    // Week navigation
    shiftWeek(weeks) {
      this.weekEnding = ymd(addDays(parseYmd(this.weekEnding), weeks * 7));
      this.getJobNumbers();
    },

    goToDate(date) {
      const weekEnding = weekEndingFor(date);
      if (weekEnding === this.weekEnding) return;
      this.weekEnding = weekEnding;
      this.getJobNumbers();
    },

    getOptions(id) {
      if (id && this.materials.length > 0) {
        const foundMaterial = this.materials.find((e) => e.jobnumber == id);
        return foundMaterial ? foundMaterial.partNames : [];
      }
      return [];
    },

    // Totals
    dayTotal(user, day) {
      return user.jobData.reduce((total, row) => total + (parseFloat(row[day]) || 0), 0);
    },

    userTotal(user) {
      return DAY_KEYS.reduce((total, day) => total + this.dayTotal(user, day.key), 0);
    },

    formatHours(value) {
      return Math.round(value * 100) / 100;
    },

    // Materials
    materialCount(user) {
      return (user.materialEntries || []).length;
    },

    // Entries without a unit cost are left out rather than counted as $0.
    userMaterialsCost(user) {
      const total = (user.materialEntries || []).reduce((sum, row) => {
        const quantity = parseFloat(row.quantity);
        const unitCost = parseFloat(row.unitCost);
        return Number.isNaN(quantity) || Number.isNaN(unitCost) ? sum : sum + Math.round(quantity * unitCost * 100) / 100;
      }, 0);
      return Math.round(total * 100) / 100;
    },

    formatMoney(value) {
      return money.format(value);
    },

    // A material typed on one row becomes pickable on every row.
    addCatalogueMaterial(material) {
      if (!material || this.materialCatalogue.some((m) => m.id === material.id)) return;
      this.materialCatalogue = [...this.materialCatalogue, material].sort((a, b) => a.name.localeCompare(b.name));
    },

    // Rows
    addNewRow(user) {
      user.jobData.push(blankRow());
    },

    onEnter(user, index, day) {
      if (index === user.jobData.length - 1) this.addNewRow(user);
      this.$nextTick(() => {
        const next = this.$el.querySelector(`[data-cell="${user.id}-${index + 1}-${day}"]`);
        if (next) next.focus();
      });
    },

    finalizedTitle(row) {
      return row.editable
        ? `Billing for job ${row.jobId} is finalized. As a manager you can still change these hours.`
        : `Billing for job ${row.jobId} is finalized, so these hours are locked.`;
    },

    saveStateTitle(row) {
      return { saving: 'Saving…', saved: 'Saved', error: 'Not saved, change a value to try again' }[row.saveState] || '';
    },

    saveRowData(row, user) {
      this.$set(row, 'saveState', 'saving');

      const run = () => {
        const rowData = {
          id: row.id,
          reason: row.reason,
          billable: row.billable,
          category: row.category,
          jobId: row.jobId,
          weekEnding: this.weekEnding,
          userId: user.id,
        };
        DAY_KEYS.forEach((day) => { rowData[day.key] = parseFloat(row[day.key]) || 0; });

        return store.dispatch('user/saveRowData', { rowData }).then((response) => {
          if (!row.id && response.data && response.data.id) this.$set(row, 'id', response.data.id);
          row.saveState = 'saved';
          setTimeout(() => {
            if (row.saveState === 'saved') row.saveState = null;
          }, 2000);
        }).catch((error) => {
          console.error(error);
          row.saveState = 'error';
          this.toast('Not saved', this.errorMessage(error, 'This row could not be saved. Change a value to try again.'), 'danger');
        });
      };

      const queued = (this.saveQueues.get(row) || Promise.resolve()).then(run);
      this.saveQueues.set(row, queued);
      return queued;
    },

    isOwn(user) {
      return user.id === this.currentUserId;
    },

    // Your own timesheet starts open and everyone else's starts collapsed, unless toggled on this visit.
    isCollapsed(user) {
      if (this.ownOnly) return false;
      if (Object.prototype.hasOwnProperty.call(this.collapseChoices, user.id)) return this.collapseChoices[user.id];
      return !this.isOwn(user);
    },

    toggleUser(user) {
      this.collapseChoices = { ...this.collapseChoices, [user.id]: !this.isCollapsed(user) };
    },

    setBillable(row, user, billable) {
      if (!!row.billable === billable) return;
      row.billable = billable;
      row.jobId = null;
      row.category = null;
      row.reason = null;
      if (row.id) this.saveRowData(row, user);
    },

    rowSummary(row) {
      const hours = this.formatHours(DAY_KEYS.reduce((total, day) => total + (parseFloat(row[day.key]) || 0), 0));
      const what = row.billable ? (row.jobId ? `job ${row.jobId}` : 'a billable row') : row.category || row.reason || 'a non-billable row';
      return `${what}, ${hours} h`;
    },

    async deleteRow(user, row) {
      const result = await this.$swal({
        title: 'Delete this row?',
        text: `This removes ${this.rowSummary(row)} from ${user.name}'s timesheet.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        confirmButtonColor: '#ea5455',
      });
      if (!result.isConfirmed) return;

      if (row.id) {
        try {
          await store.dispatch('user/deleteRowData', { rowData: row });
        } catch (error) {
          console.error(error);
          this.toast('Not deleted', this.errorMessage(error, 'The row could not be deleted.'), 'danger');
          return;
        }
      }
      user.jobData.splice(user.jobData.indexOf(row), 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.week-nav {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #2f2f2f;
  color: #fff;
}

// The switch label lives inside the BFormCheckbox child component, so it needs ::v-deep.
.week-nav ::v-deep .custom-control-label {
  color: #fff;
  cursor: pointer;
}

.week-arrow {
  color: #fff !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
  padding: 4px 8px;
}

.week-arrow:hover {
  background: rgba(255, 255, 255, 0.12) !important;
}

.week-label {
  display: flex;
  flex-direction: column;
  min-width: 210px;
  text-align: center;
  line-height: 1.2;
}

.week-label .text-muted {
  color: rgba(255, 255, 255, 0.65) !important;
}

.week-title {
  font-size: 16px;
  font-weight: 700;
}

.week-nav .btn-flat-primary {
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
}

.week-nav .btn-flat-primary:disabled {
  opacity: 0.45;
}

.week-picker {
  width: 150px;
  background-color: #fff !important;
}

.user-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 0.5rem;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  font-weight: inherit;
  text-transform: inherit;
  cursor: pointer;
}

.user-toggle:focus-visible {
  outline: 2px solid #fff;
  outline-offset: -4px;
}

.you-tag {
  padding: 0 8px;
  border-radius: 999px;
  background: #a51c30;
  color: #fff;
  font-size: 10px;
  line-height: 18px;
  letter-spacing: 0.5px;
}

// Your own timesheet on the Timesheet page: a frame in the header bars' dark colour so it stands out
// from everyone else's. The "You" tag keeps its crimson.
$own-accent: #303030;

.own-section {
  margin: 0.75rem 15px 1.5rem;
  padding: 2px 12px 4px;
  flex: 0 0 calc(100% - 30px);
  max-width: calc(100% - 30px);
  border: 2px solid $own-accent;
  border-radius: 10px;
  background: rgba($own-accent, 0.03);
  box-shadow: 0 4px 14px rgba($own-accent, 0.18);
}

.user-week-total {
  font-weight: 400;
  font-size: 12px;
  opacity: 0.75;
  text-transform: none;
}

.user-week-total.has-hours {
  opacity: 1;
  font-weight: 600;
}

.billable-toggle {
  display: inline-flex;
  padding: 2px;
  border-radius: 999px;
  background: #f3f2f7;
}

.billable-toggle button {
  border: none;
  background: none;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #82868b;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}

.billable-toggle button.active {
  background: #28c76f;
  color: #fff;
}

.billable-toggle button.active.off {
  background: #82868b;
}

.billable-toggle button:disabled {
  cursor: default;
  opacity: 0.6;
}

.billable-toggle button:focus-visible {
  outline: 2px solid #a51c30;
  outline-offset: 1px;
}

.over-eight {
  display: inline-block;
  padding: 0 8px;
  border-radius: 999px;
  background: #ea5455;
  color: #fff;
  font-weight: 700;
  cursor: help;
}

.day-date {
  font-weight: 400;
  color: #82868b;
}

.save-state {
  display: inline-block;
  width: 18px;
  margin-right: 6px;
  vertical-align: middle;
}

// Amber: finalized but a manager can still edit. Grey: locked.
.finalized-flag {
  display: inline-block;
  margin-right: 4px;
  vertical-align: middle;
  color: #ff9f43;
  cursor: help;
}

.finalized-flag.locked {
  color: #82868b;
}

.save-state.saved {
  color: #28c76f;
}

.save-state.error {
  color: #ea5455;
}
</style>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.title {
  font-weight: 600;
}

.border {
  padding: 0.5rem;
}

.bigTitle {
  color: #a51c30;

  font-size: 1.5rem;
}
// text-center mt-5 p-1 bg-light-secondary
.add-new-line-button {
  // text-align: center;
  margin-top: 2rem;
  // height: 3rem;
  // border: 1px solid #a51c3050;
  // border-style: dashed;
  // cursor: pointer;

  outline: none;
  display: inline-flex;

  line-height: 1.5;
  font-size: 14px;
  cursor: pointer;
  font-weight: 400;
  -webkit-box-pack: center;
  justify-content: center;
  vertical-align: middle;
  -webkit-box-align: center;
  align-items: center;
  border: 1px dashed #a51c3080;
  height: 3rem;
  padding: 0px 1.5rem;
  width: 100%;
  background: rgb(247, 250, 255);
  color: #a51c30;
}

.add-new-line-button:hover {
  background: rgb(243, 245, 247);
  color: rgb(97, 113, 131);
}

.quoteInput {
  border: none;
  background-color: #ffffff;
  border-bottom: 1px solid lightgray;
  text-align: center;
}

.form-control:focus {
  border-color: #a51c30 !important;
}

.form-control {
  padding: 0px;
}

#quil-content ::v-deep {
  > .ql-container {
    .ql-editor {
      height: 300px !important;
    }
  }
}

#quil-content ::v-deep {
  > .ql-container {
    border-bottom: 0;
    border-top-right-radius: 0rem;
    border-top-left-radius: 0rem;
    background-color: white;
  }

  + #quill-toolbar {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  + #comment-toolbar {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
}

.prependIcon {
  border: none;
  font-size: 1.2rem;
  margin-right: 0px;
  padding-right: 3px;
  // margin-top: 2px;

  border-bottom: 1px solid lightgray;
}

.uploadButton {
  position: absolute;
  top: -8px;
  right: 85px;
  margin: 2px;
  padding: 3px;
  border-radius: 6px;
  color: #fff;
  background-color: #ff0000;
  cursor: pointer;
  opacity: 0.5;
}
.uploadButton:hover {
  opacity: 1;
}
.btn-file {
  position: relative;
  overflow: hidden;
}
.btn-file input[type='file'] {
  position: absolute;
  top: 0;
  right: 0;
  min-width: 100%;
  min-height: 100%;
  font-size: 100px;
  text-align: right;
  filter: alpha(opacity=0);
  opacity: 0;
  outline: none;
  background: white;
  cursor: inherit;
  display: block;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
